import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Modal } from "@/components/ui/Modal";
import { SpinnerIcon, RefreshIcon, CheckIcon } from "@/components/icons";
import { copyToClipboard } from "@/utils/clipboard";
import { apiClient } from "@/services/apiClient";

type SaveStatus = "idle" | "saving" | "saved" | "error";

interface ReportSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
}

export const ReportSummaryModal: React.FC<ReportSummaryModalProps> = ({
  isOpen,
  onClose,
  clientId,
}) => {
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string>("");

  const year = parseInt(searchParams.get("year") || String(new Date().getFullYear()));
  const month = parseInt(searchParams.get("month") || String(new Date().getMonth() + 1));

  const saveSummary = useCallback(async (text: string) => {
    if (text === lastSavedRef.current) return;
    setSaveStatus("saving");
    try {
      await apiClient.patch(
        `/api/social-media/reports/${clientId}/summary`,
        { year, month, summary: text }
      );
      lastSavedRef.current = text;
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus((s) => s === "saved" ? "idle" : s), 2000);
    } catch {
      setSaveStatus("error");
    }
  }, [clientId, year, month]);

  const handleSummaryChange = useCallback((text: string) => {
    setSummary(text);
    setSaveStatus("idle");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveSummary(text), 1000);
  }, [saveSummary]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (isOpen && clientId) {
      fetchSummary(false);
    }
    if (!isOpen) {
      // Flush any pending save before clearing
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        if (summary && summary !== lastSavedRef.current) {
          saveSummary(summary);
        }
      }
      setSummary("");
      setError(null);
      setShowInstructions(false);
      setInstructions("");
      setCopied(false);
      setSaveStatus("idle");
      lastSavedRef.current = "";
    }
  }, [isOpen, clientId, year, month]);

  const fetchSummary = async (regenerate: boolean) => {
    setIsLoading(true);
    setError(null);
    setCopied(false);

    try {
      const payload: Record<string, unknown> = { year, month };
      if (regenerate) {
        payload.regenerate = true;
        if (instructions.trim()) {
          payload.userInstructions = instructions.trim();
        }
      }

      const response = await apiClient.post<{ summary?: string; status?: string; gen_id?: string }>(
        `/api/social-media/reports/${clientId}/summary`,
        payload
      );

      if (response.summary) {
        // Cached summary returned immediately
        setSummary(response.summary);
        lastSavedRef.current = response.summary;
        if (regenerate) {
          setShowInstructions(false);
          setInstructions("");
        }
        setIsLoading(false);
        return;
      }

      // 202 response: generation in background, poll for result
      if (response.status === "generating") {
        const maxAttempts = 30;
        const pollInterval = 2000;
        for (let i = 0; i < maxAttempts; i++) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));
          try {
            const pollResult = await apiClient.post<{ summary?: string; status?: string }>(
              `/api/social-media/reports/${clientId}/summary`,
              { year, month }
            );
            if (pollResult.summary) {
              setSummary(pollResult.summary);
              lastSavedRef.current = pollResult.summary;
              if (regenerate) {
                setShowInstructions(false);
                setInstructions("");
              }
              break;
            }
          } catch {
            // Keep polling
          }
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to generate summary";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!summary) return;
    const ok = await copyToClipboard(summary);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRegenerate = () => {
    fetchSummary(true);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Riepilogo Report"
      size="lg"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Chiudi
          </button>
          <button
            onClick={() => setShowInstructions((v) => !v)}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshIcon size={16} className="mr-1.5" />
            Rigenera
          </button>
          <button
            onClick={handleCopy}
            disabled={isLoading || !summary}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {copied ? (
              <>
                <CheckIcon size={16} className="mr-1.5" />
                Copiato!
              </>
            ) : (
              "Copia"
            )}
          </button>
        </>
      }
    >
      <div className="p-6 space-y-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <SpinnerIcon size={32} className="text-blue-600 mb-3" />
            <p className="text-sm text-gray-500">Generazione riepilogo in corso...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => fetchSummary(false)}
              className="mt-2 text-sm text-red-600 underline hover:text-red-800"
            >
              Riprova
            </button>
          </div>
        ) : (
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={summary}
              onChange={(e) => handleSummaryChange(e.target.value)}
              rows={16}
              className="w-full rounded-lg border border-gray-300 p-4 text-sm leading-relaxed text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-y"
            />
            {saveStatus !== "idle" && (
              <span className={`absolute bottom-2 right-3 text-xs ${
                saveStatus === "saving" ? "text-gray-400" :
                saveStatus === "saved" ? "text-green-500" :
                "text-red-500"
              }`}>
                {saveStatus === "saving" && "Salvataggio..."}
                {saveStatus === "saved" && "Salvato"}
                {saveStatus === "error" && "Errore nel salvataggio"}
              </span>
            )}
          </div>
        )}

        {showInstructions && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Istruzioni per la rigenerazione (opzionale)
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
              placeholder="Es: Enfatizza di piu la crescita su Instagram, meno dettagli su Facebook..."
              className="w-full rounded-lg border border-gray-300 p-3 text-sm text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-y"
            />
            <div className="flex justify-end">
              <button
                onClick={handleRegenerate}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? (
                  <SpinnerIcon size={16} className="mr-1.5" />
                ) : (
                  <RefreshIcon size={16} className="mr-1.5" />
                )}
                Genera
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
